import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserFormData, Store, User } from "@/types";
import { getStoresFromDatabase } from "@/services/store";
import UserFormFields from "./UserFormFields";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingUser: User | null;
  onUserSaved: () => Promise<void>;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  editingUser,
  onUserSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    avatar: '',
    role: 'user',
    points: 0,
    status: 'active',
    connectedAccount: '',
    mainStoreId: '',
  });

  const isEditing = !!editingUser;

  // Load stores when component mounts
  useEffect(() => {
    const loadStores = async () => {
      const storesData = await getStoresFromDatabase();
      setStores(storesData);
    };

    if (isOpen) {
      loadStores();
    }
  }, [isOpen]);

  // Populate form when editing user
  useEffect(() => {
    if (editingUser) {
      setFormData({
        name: editingUser.username || editingUser.name || '',
        email: editingUser.email || '',
        avatar: editingUser.avatar_url || editingUser.avatar || '',
        role: editingUser.role || 'user',
        points: editingUser.points || 0,
        status: editingUser.status || 'active',
        connectedAccount: editingUser.connectedAccountId || '',
        mainStoreId: editingUser.main_store_id || '',
      });
    } else {
      // Reset form for new user
      setFormData({
        name: '',
        email: '',
        avatar: '',
        role: 'user',
        points: 0,
        status: 'active',
        connectedAccount: '',
        mainStoreId: '',
      });
    }
  }, [editingUser, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'points' ? Number(value) : value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing && editingUser) {
        // Update existing user
        const { error } = await supabase
          .from('profiles')
          .update({
            username: formData.name,
            full_name: formData.name,
            email: formData.email,
            avatar_url: formData.avatar,
            role: formData.role,
            points: formData.points,
            connected_account_id: formData.connectedAccount || null,
            main_store_id: formData.mainStoreId || null,
            status: formData.status,
          })
          .eq('id', editingUser.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Utilizador actualizado com sucesso",
        });
      } else {
        // Create new user via Edge Function
        const { data, error: createError } = await supabase.functions.invoke('admin-create-user', {
          body: {
            email: formData.email,
            name: formData.name,
            avatar: formData.avatar,
            role: formData.role,
            points: formData.points,
            status: formData.status,
            connectedAccount: formData.connectedAccount,
            mainStoreId: formData.mainStoreId,
          }
        });

        if (createError) {
          throw new Error(`Erro ao criar utilizador: ${createError.message}`);
        }

        if (!data?.success) {
          // Use the specific error message from the Edge function
          throw new Error(data?.error || 'Erro desconhecido ao criar utilizador');
        }

        toast({
          title: "Sucesso",
          description: "Utilizador criado com sucesso",
        });
      }

      // Reload users and close modal
      await onUserSaved();
      onClose();
    } catch (err: any) {
      // Check if we have a specific error message from the Edge function
      let userMessage = err.message;

      // If the error message starts with "Erro ao criar utilizador:", extract the actual message
      if (err.message?.startsWith('Erro ao criar utilizador:')) {
        userMessage = err.message.replace('Erro ao criar utilizador: ', '');
      }

      // Fallback error handling for other cases
      if (!userMessage || userMessage === "Ocorreu um erro ao processar a requisição.") {
        if (err.message?.includes('RLS') || err.message?.includes('policy')) {
          userMessage = "Erro de permissões. Verifique se tem privilégios de administrador.";
        } else if (err.message?.includes('duplicate') || err.message?.includes('already exists')) {
          userMessage = "Este email já está em uso por outro utilizador.";
        } else if (err.message?.includes('network') || err.message?.includes('connection')) {
          userMessage = "Erro de conexão. Tente novamente em alguns instantes.";
        } else if (err.message?.includes('Unauthorized')) {
          userMessage = "Não tem permissões suficientes para criar utilizadores.";
        } else {
          userMessage = "Ocorreu um erro ao processar a requisição.";
        }
      }

      toast({
        title: "Erro",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] bg-brand-gray border-0 overflow-y-auto p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">
            {isEditing ? `Editar Utilizador: ${editingUser?.username || editingUser?.name}` : 'Adicionar Novo Utilizador'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <UserFormFields
            formData={{...formData, id: editingUser?.id}}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            isEditing={isEditing}
            stores={stores}
          />

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="bg-transparent border-gray-400 text-white hover:bg-gray-600 hover:border-gray-300"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-brand-red hover:bg-brand-red/90 text-white"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-b-transparent"></span>
                  A gravar...
                </span>
              ) : isEditing ? (
                "Actualizar Utilizador"
              ) : (
                "Adicionar Utilizador"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;